import type { QueryClient } from '@tanstack/react-query';
import { useAssistantDockStore } from '@/stores/assistantDockStore';
import { CASE_MEMORY_KEY } from '@/hooks/useCaseMemories';
import { MEMORY_SETTINGS_KEY, memorySettingsKey } from '@/hooks/useMemorySettings';
import { PIECE_TITLE_PROMPT_KEY, pieceTitlePromptKey } from '@/hooks/useNomenclatureSettings';
import {
  VOICE_TRANSCRIPTION_SETTINGS_KEY,
  voiceTranscriptionSettingsKey,
} from '@/hooks/useVoiceTranscriptionSettings';
import { REDACTION_STYLE_KEY, redactionStyleKey } from '@/hooks/useRedactionStyle';
import { V3_CONVERSATION_HISTORY_KEY } from '../hooks/useConversationHistory';
import { V3_CONVERSATION_MEMORY_USAGE_KEY } from '../hooks/useConversationMemoryUsage';
import { oublierLesFichiersRendusDeLaConversation } from '../state/fichiersRendusAuComposeur';
import { useActiveSessionsStore } from '../stores/activeSessionsStore';
import { useConversationTranscriptStore } from '../stores/conversationTranscriptStore';
import type { SessionDoneSnapshot } from '../types/transcript.types';

export function handleSessionDoneSnapshot(
  snapshot: SessionDoneSnapshot,
  queryClient: QueryClient,
): void {
  // Tour repris au chargement de page (#1888) : la timeline locale n'a que ce qui a été
  // émis après le rechargement. La valider figerait une réponse amputée de son début —
  // un transcript `ready` refuse ensuite toute réhydratation, il fallait recharger une
  // seconde fois. Le backend persiste la réponse avant d'émettre `agent_done` : même
  // règlement que `turn_settled`, la base fait foi.
  // WHY seulement `complete` : sur `stopped` / `timeout` / `error`, la note ou le bandeau
  // qui explique la fin du tour n'existe QUE dans la timeline locale ; recharger
  // l'effacerait. Le fragment reste alors affiché, avec sa raison.
  if (snapshot.resumedOnLoad && snapshot.reason === 'complete') {
    handleTurnSettled(snapshot.conversationId, snapshot.localTurnId, queryClient);
    return;
  }
  useConversationTranscriptStore.getState().commitTurn(snapshot);
  void queryClient.invalidateQueries({ queryKey: ['conversations'] });
}

/**
 * Rafraîchit les réglages de Personnalisation à la fin d'un tour, quand le dock
 * HORS dossier est ouvert.
 *
 * WHY turn-scoped et pas tool-scoped : le déclencheur juste serait un événement
 * backend `personalization_mutated` émis par `set_voice_transcription_prompt`,
 * `set_piece_naming_rules` et les outils de mémoire — exactement comme
 * `skills_mutated` ou `case_documents_mutated` le font déjà pour leurs surfaces.
 * Cet événement **exige une modification backend qui n'a pas été autorisée** pour
 * ce chantier (front-only). On rafraîchit donc à la fin de CHAQUE tour joué
 * pendant que le dock hors dossier est ouvert : c'est le sur-ensemble le plus
 * étroit qu'on puisse observer depuis le front. Chemin de mise à niveau pour le
 * prochain lecteur : ajouter le frame backend, l'écouter dans
 * `WebSocketPoolProvider` comme les autres `*_mutated`, et supprimer cet appel.
 *
 * WHY proportionné : le dock hors dossier n'existe QUE pour les pages de type
 * réglages (c'est son point de montage, cf. `assistantDockStore`), donc un tour
 * qui s'y termine a de bonnes chances d'avoir écrit un réglage. Le coût est de
 * deux petits GET (consigne de dictée, règles de nommage) plus la mémoire, dont
 * la liste « Vos dossiers » n'est un fan-out que si l'onglet Mémoire est monté —
 * `invalidateQueries` ne refetch que les queries ACTIVES.
 *
 * Le dock DOSSIER est volontairement exclu : ses tours parlent du dossier, pas
 * des réglages, et ils sont déjà couverts par `case_documents_mutated`.
 */
export function refreshPersonalizationAfterCaselessDockTurn(
  queryClient: QueryClient,
  cabinetId: string | null,
): void {
  const dock = useAssistantDockStore.getState();
  if (!dock.isOpen || dock.caseId !== null) return;

  // Convention monorepo : invalider la clé spécifique ET la générale.
  if (cabinetId) {
    void queryClient.invalidateQueries({ queryKey: voiceTranscriptionSettingsKey(cabinetId) });
    void queryClient.invalidateQueries({ queryKey: pieceTitlePromptKey(cabinetId) });
    void queryClient.invalidateQueries({ queryKey: memorySettingsKey(cabinetId) });
    // WHY `redaction-style` ici : `set_redaction_style` (toujours actif côté serveur) peut
    // réécrire le Style.md pendant que l'écran Charte graphique → Style est affiché.
    // Sans cette invalidation, la query n'est jamais relancée (plus aucun remontage ne
    // le fait depuis que la barre ouvre le dock au lieu de naviguer vers /assistant), et
    // l'avocat lirait l'ancienne plume alors qu'Hector confirme avoir appliqué les changements.
    void queryClient.invalidateQueries({ queryKey: redactionStyleKey(cabinetId) });
  }
  void queryClient.invalidateQueries({ queryKey: [VOICE_TRANSCRIPTION_SETTINGS_KEY] });
  void queryClient.invalidateQueries({ queryKey: [PIECE_TITLE_PROMPT_KEY] });
  void queryClient.invalidateQueries({ queryKey: [MEMORY_SETTINGS_KEY] });
  void queryClient.invalidateQueries({ queryKey: [REDACTION_STYLE_KEY] });
  // Mémoire-dossier : une seule clé, scopée par `caseId` et non par cabinet — on
  // ne sait pas lequel l'agent a touché, donc on balaie le préfixe.
  void queryClient.invalidateQueries({ queryKey: [CASE_MEMORY_KEY] });
}

/**
 * Fin d'un tour suivi à distance (`turn_settled`, 2026-09-03) : le backend a tout persisté,
 * le tour local n'est qu'un fragment. Ordre imposé par le store : `rollbackTurn` d'abord
 * (`hydrateTranscript` refuse de réhydrater tant qu'un `activeTurn` existe), puis `markStale`
 * (un transcript `ready` n'est pas réhydraté non plus), puis invalidation de l'historique
 * exact — refetch seulement s'il est observé — et de la liste de conversations.
 */
export function handleTurnSettled(
  conversationId: string,
  localTurnId: string | null,
  queryClient: QueryClient,
): void {
  if (localTurnId) {
    useConversationTranscriptStore.getState().rollbackTurn(conversationId, localTurnId);
  }
  handleConversationHistoryStale(conversationId, queryClient);
  void queryClient.invalidateQueries({ queryKey: ['conversations'] });
}

/**
 * La base REST fait de nouveau foi pour cette conversation : transcript marqué
 * périmé (un transcript `ready` refuse toute réhydratation) puis invalidation de
 * l'historique exact — refetch seulement s'il est observé.
 *
 * Point d'entrée aussi de `conversation_history_stale` : une compaction manuelle
 * change l'historique persisté (messages archivés de nouveau visibles, marqueur
 * de compaction) HORS de tout tour local, donc aucun commit local ne peut le montrer.
 */
export function handleConversationHistoryStale(
  conversationId: string,
  queryClient: QueryClient,
): void {
  useConversationTranscriptStore.getState().markStale(conversationId);
  void queryClient.invalidateQueries({
    queryKey: [V3_CONVERSATION_HISTORY_KEY, conversationId],
    refetchType: 'active',
  });
  // Même moment où la jauge live peut disparaître (session retirée) ou changer hors
  // tour (compaction manuelle) : la jauge persistée doit être relue, sinon le rond
  // retomberait sur la valeur mise en cache à l'ouverture.
  void queryClient.invalidateQueries({
    queryKey: [V3_CONVERSATION_MEMORY_USAGE_KEY, conversationId],
    refetchType: 'active',
  });
}

/**
 * La session d'une conversation a quitté le pool (inactivité, place reprise par
 * une autre conversation, conversation supprimée) : ce que l'onglet gardait de
 * local pour elle cesse de faire foi.
 */
export function handleSessionRemoved(
  sessionKey: string,
  queryClient: QueryClient,
): void {
  const conversationId = useActiveSessionsStore.getState().sessions[sessionKey]?.conversationId;
  if (!conversationId) {
    return;
  }

  handleConversationHistoryStale(conversationId, queryClient);
  // Les fichiers qu'un message de ce fil n'a pas emportés n'attendaient que le
  // retour de l'avocat sur ce fil. Une conversation fermée dans l'onglet ne les
  // retient plus : collés ou convertis, ils pèsent jusqu'à 50 Mo chacun en
  // mémoire (#2066). C'est aussi le chemin de la suppression, qui ferme la
  // session par `WebSocketPool.closeConversation`.
  oublierLesFichiersRendusDeLaConversation(conversationId);
}
